import { useContext, useEffect, useState } from "react";
import "../../../../styles/workspace_styles/CustomerAdministratorStyles/Accounts/Security.css";
import { AppContext } from "../../../AppProvider";
import JwtDecodeService from "../../../../services/JwtDecodeService";
import { LoadShortDate } from "../../../../functions/LoadShortDate";
import { LoadShortTime } from "../../../../functions/LoadShortTime";
import { usersMicroserviceUrl } from "../../../../microservices/users_microservice";
import { thingsBoardMicroserviceUrl } from "../../../../microservices/thingsboard_microservice";
import { Eye, EyeOff } from "lucide-react";
const Security = () =>{
    const {user,userProfile,customerIotProfile,logout,setShowBottomRightMessageBox,setBottomRightMessageBoxMessage} = useContext(AppContext);
    const [APIKey,setAPIKey] = useState(null);
    const [showCurrentPassword,setShowCurrentPassword] = useState(false);
    const [showNewPassword,setShowNewPassword] = useState(false);
    const [showConfirmNewPassword,setShowConfirmNewPassword] = useState(false);
    const [currentPasswordEntry,setCurrentPasswordEntry] = useState('');
    const [newPasswordEntry,setNewPasswordEntry] = useState('');
    const [confirmNewPasswordEntry,setConfirmNewPasswordEntry] = useState('');
    const [changePasswordErrorMessage,setChangePasswordErrorMessage] = useState('');
    const [changingPassword,setChangingPassword] = useState(null);
    const createAPIKey = async() =>{
        try{
            const url = `${thingsBoardMicroserviceUrl}/customers/users/shared/${userProfile?.emailAddress}/api/key`;
            const response = await fetch(url,{
                method:'POST',
                headers:{
                    'Content-Type':'application/json',
                    'Authorization':`Bearer ${user?.access_token}`
                }
            });
            if(response.ok) setAPIKey(await response.json());
            else if(response.status === 401) logout();
            else throw new Error(`response status code: ${response.status}`);
        }
        catch(error){
            console.error('An error occurred when creating API Key',error);
        }
    };
    const fetchAPIKey = async() =>{
        try{
            const url = `${thingsBoardMicroserviceUrl}/customers/users/shared/${userProfile?.emailAddress}/api/key`;
            const response = await fetch(url,{
                method:'GET',
                headers:{
                    'Content-Type':'application/json',
                    'Authorization':`Bearer ${user?.access_token}`
                }
            });
            if(response.ok) setAPIKey(await response.json());
            else if(response.status === 401) logout();
            else throw new Error(`response status code: ${response.status}`);
        }
        catch(error){
            console.error('An error occurred when fetching API Key',error);
        }
    };
    const deleteAPIKey = async() =>{
        try{
            const url = `${thingsBoardMicroserviceUrl}/customers/users/shared/${userProfile?.emailAddress}/api/key`;
            const response = await fetch(url,{
                method:'DELETE',
                headers:{
                    'Content-Type':'application/json',
                    'Authorization':`Bearer ${user?.access_token}`
                }
            });
            if(response.ok) setAPIKey(null);
            else if(response.status === 401) logout();
            else throw new Error(`response status code: ${response.status}`);
        }
        catch(error){
            console.error('An error occurred when deleting API Key',error);
        }
    };
    const changeAccountPassword = async() =>{
        setChangingPassword('changing');
        try{
            const payload = {
                currentPassword:currentPasswordEntry,
                newPassword:newPasswordEntry
            };
            const url = `${usersMicroserviceUrl}/passwords`;
            const response = await fetch(url,{
                method:'PATCH',
                headers:{
                    'Authorization':`Bearer ${user?.access_token}`,
                    'Content-Type':'application/json'
                },
                body:JSON.stringify(payload)
            });
            if(response.ok) setChangingPassword('changed');
            else if(response.status === 401) logout();
            else if(response.status === 403) setChangingPassword('forbidden');
            else setChangingPassword('error');
        }
        catch(error){
            console.error('Unexpected error occurred when updating user password',error);
            setChangingPassword('error');
        }
    };
    const getTokenExpiryDate = () =>{
        const decodedToken = JwtDecodeService(user?.access_token);
        return `${LoadShortDate(decodedToken?.exp * 1000)} at ${LoadShortTime(decodedToken?.exp * 1000)}`;
    };
    const showCopyAccessTokenNotification = () =>{
        setBottomRightMessageBoxMessage("Your account access token has been copied to clipboard");
        setShowBottomRightMessageBox(true);
    };
    const showPrintAccessTokenNotification = () =>{
        setBottomRightMessageBoxMessage("Your account access token has been printed on the console");
        setShowBottomRightMessageBox(true);
    };
    const showCopyAPIKeyNotification = () =>{
        setBottomRightMessageBoxMessage("Your API Key has been copied to clipboard");
        setShowBottomRightMessageBox(true);
    };
    const showPrintAPIKeyNotification = () =>{
        setBottomRightMessageBoxMessage("Your API Key has been printed on the console");
        setShowBottomRightMessageBox(true);
    };
    const showCopyCustomerIdNotification = () =>{
        setBottomRightMessageBoxMessage("Your customer ID has been copied to clipboard");
        setShowBottomRightMessageBox(true);
    };
    const showPrintCustomerIdNotification = () =>{
        setBottomRightMessageBoxMessage("Your customer ID has been printed on the console");
        setShowBottomRightMessageBox(true);
    };
    const copyAccessTokenToClipboard = () =>{
        if(navigator.clipboard){
            navigator.clipboard.writeText(user?.access_token);
            showCopyAccessTokenNotification();
        }
        else{
            console.log(user?.access_token);
            showPrintAccessTokenNotification();
        }
    };
    const copyAPIKeyToClipboard = () =>{
        if(navigator.clipboard){
            navigator.clipboard.writeText(APIKey?.value);
            showCopyAPIKeyNotification();
        }
        else{
            console.log(APIKey?.value);
            showPrintAPIKeyNotification();
        }
    };
    const copyCustomerIdToClipboard = () =>{
        if(navigator.clipboard){
            navigator.clipboard.writeText(customerIotProfile?.id);
            showCopyCustomerIdNotification();
        }
        else{
            console.log(customerIotProfile?.id);
            showPrintCustomerIdNotification();
        }
    };
    const discardPasswordChanges = () =>{
        setCurrentPasswordEntry('');
        setNewPasswordEntry('');
        setConfirmNewPasswordEntry('');
    };
    useEffect(() =>{
        fetchAPIKey();
    },[]);
    return(
        <div className="customer-administrator-account-security-container">
            <div className="customer-administrator-account-security-token-container">
                <h2>JWT token</h2>
                <div>
                    <p>Token is valid till {getTokenExpiryDate()}</p>
                    <button onClick={() => copyAccessTokenToClipboard()}>Copy JWT token</button>
                </div>
            </div>
            {APIKey? (
                <div className="customer-administrator-account-security-api-key-container">
                    <h2>API Key</h2>
                    <div>
                        <p>API Key is valid till {APIKey?.expiryDate} at {APIKey?.expiryTime}</p>
                        <div className="buttons-container">
                            <button className="regenerate-button" onClick={async() => await createAPIKey()}>Regenerate API Key</button>
                            <button onClick={async() => await deleteAPIKey()}>Delete API Key</button>
                            <button onClick={() => copyAPIKeyToClipboard()}>Copy API Key</button>
                        </div>
                    </div>
                </div>
            ):(<div className="customer-administrator-account-security-api-key-container">
                <h2>API Key</h2>
                <p>Your account does not have an API Key. Click generate to get one.</p>
                <div>
                    <button onClick={async() => await createAPIKey()}>Generate API Key</button>
                </div>
            </div>)}
            <div className="customer-administrator-account-security-customer-id-container">
                <h2>Customer ID</h2>
                <div>
                    <p>Your ID is: {customerIotProfile?.id}</p>
                    <button onClick={() => copyCustomerIdToClipboard()}>Copy ID</button>
                </div>
            </div>
            <div className="customer-administrator-account-security-password-change-container">
                <div className="customer-administrator-account-security-password-change-entries-container">
                    <h2>Change Password</h2>
                    <div className="customer-administrator-account-security-password-change-entry-container">
                        <p>Current password<span>*</span></p>
                        <div>
                            <input
                                type={showCurrentPassword? 'text':'password'}
                                value={currentPasswordEntry}
                                onChange={(e) => setCurrentPasswordEntry(e.target.value)}
                            />
                            <button onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                title={showCurrentPassword? 'Hide password':'Show password'}>
                                {showCurrentPassword? <EyeOff/>:<Eye/>}
                            </button>
                        </div>
                    </div>
                    <div className="customer-administrator-account-security-password-change-entry-container">
                        <p>New password<span>*</span></p>
                        <div>
                            <input
                                type={showNewPassword? 'text':'password'}
                                value={newPasswordEntry}
                                onChange={(e) => setNewPasswordEntry(e.target.value)}
                            />
                            <button onClick={() => setShowNewPassword(!showNewPassword)}
                                title={showNewPassword? 'Hide password':'Show password'}>
                                {showNewPassword? <EyeOff/>:<Eye/>}
                                </button>
                        </div>
                    </div>
                    <div className="customer-administrator-account-security-password-change-entry-container">
                        <p>Confirm new password<span>*</span></p>
                        <div>
                            <input
                                type={showConfirmNewPassword? 'text':'password'}
                                value={confirmNewPasswordEntry}
                                onChange={(e) => setConfirmNewPasswordEntry(e.target.value)}
                            />
                            <button onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                                title={showConfirmNewPassword? 'Hide password':'Show password'}>
                                {showConfirmNewPassword? <EyeOff/>:<Eye/>}
                            </button>
                        </div>
                    </div>
                </div>
                <div className="customer-administrator-account-security-password-change-requirements-container">
                    <h2>Password requirements</h2>
                    <div>
                        <h3>At least:</h3>
                        {newPasswordEntry.length < 6?
                            (<ul>
                                <li>6 characters</li>
                            </ul>):(
                                <p><span>&#10003;</span>6 characters</p>
                            )
                        }
                    </div>
                    <div>
                        <h3>At most:</h3>
                        {newPasswordEntry.length < 1 || newPasswordEntry.length > 64?
                            (<ul>
                                <li>64 characters</li>
                            </ul>):(
                                <p><span>&#10003;</span>64 characters</p>
                            )
                        }
                    </div>
                </div>
                <div className="customer-administrator-account-security-password-change-buttons">
                    <button onClick={() => discardPasswordChanges()}>Discard changes</button>
                    <button onClick={async() =>{
                        if(currentPasswordEntry.length < 1){
                            setChangePasswordErrorMessage('Current password cannot be blank');
                            return;
                        }
                        if(newPasswordEntry.length < 6 || newPasswordEntry.length > 64){;
                            setChangePasswordErrorMessage("New password doesn't meet the requirements");
                            return;
                        }
                        if(newPasswordEntry !== confirmNewPasswordEntry){
                            setChangePasswordErrorMessage('New password does not match the confirmed');
                            return;
                        }
                        await changeAccountPassword();
                    }}>Change Password</button>
                </div>
                {
                    changePasswordErrorMessage.length > 0?
                    (
                        <div className="customer-administrator-account-security-password-change-error-parent-container">
                            <div className="customer-administrator-account-security-password-change-error-container">
                                <p>{changePasswordErrorMessage}</p>
                                <div>
                                    <button onClick={() => setChangePasswordErrorMessage('')}>Okay</button>
                                </div>
                            </div>
                        </div>
                    ):(<></>)
                }
                {   changingPassword === 'changing'? 
                    (
                        <div className="customer-administrator-account-security-password-changing-parent-container">
                            <div className="customer-administrator-account-security-password-changing-container">
                                <div className="customer-administrator-account-security-password-changing-container-spinner"></div>
                                <p>Changing password. Please wait...</p>
                            </div>
                        </div>
                    ):changingPassword === 'changed'?
                    (
                        <div className="customer-administrator-account-security-password-changed-parent-container">
                            <div className="customer-administrator-account-security-password-changed-container">
                                <h1>Password Updated</h1>
                                <p>Your password has been updated successfully.</p>
                                <div>
                                    <button onClick={() =>{
                                        discardPasswordChanges();
                                        setChangingPassword(null);
                                    }}>Okay</button>
                                </div>
                            </div>
                        </div>
                    ):changingPassword === 'forbidden'?
                    (
                        <div className="customer-administrator-account-security-password-change-forbidden-parent-container">
                            <div className="customer-administrator-account-security-password-change-forbidden-container">
                                <h1>Invalid Current Password</h1>
                                <p>The password you entered does not match the current one.<br/> Please check and try again.</p>
                                <div>
                                    <button onClick={() => setChangingPassword(null)}>Okay</button>
                                </div>
                            </div>
                        </div>
                    ):changingPassword === 'error'?
                    (
                        <div className="customer-administrator-account-security-password-change-error-parent-container">
                            <div className="customer-administrator-account-security-password-change-error-container">
                                <h1>Something Went Wrong</h1>
                                <p>Unexpected error occurred when updating your password.<br/>Please try again later.</p>
                                <div>
                                    <button onClick={() => setChangingPassword(null)}>Okay</button>
                                </div>
                            </div>
                        </div>
                    ):
                    (<></>)
                }
            </div>
        </div>
    );
};
export default Security;